import React, {useEffect, useState} from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Box, Text, HStack, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Alert, AlertIcon
} from "@chakra-ui/react";
import {api} from "../../../api/timesheetAPI";
import {useNavigate} from "react-router-dom";
import {showSwal} from "../../../common_components/Alert";
import {useAppDispatch, useAppSelector} from "../../../stateManagement/hooks";
import {jiraBoardrefreshNofilters, setTimeTableLoading, timetableRefresh} from "../../../stateManagement/refreshers";
import {Week} from "../types";
import {addDaysToAPIDate, getIsoWeekNumber} from "../../../dateUtils";
import {DEFAULT_CATEGORY, FavoriteEntry, loadFavorites} from "./favorites";
import {absenceBlockMessage, isAbsenceCodeBlocked} from "./absenceRestriction";
import {buildCopyActions, buildCopyRows, summarizeWeekForOverview, WeekOverviewSummary, WEEKDAY_NAMES} from "./rowMapping";
import {buildWorkingRows, EditableWeekTable, rowsToResult, WorkingRow} from "./EditableWeekTable";
import {ProgressOverlay} from "./ProgressOverlay";
import {saveWorklogEntry} from "./weekActionsApi";
import {formatHourString} from "./hourPrecision";
import {InfoBar} from "./WeekActionsUI";

interface CopyWeekDialogProps {
    isOpen: boolean;
    onClose: () => void;
    targetAnchor: string; // Monday of the currently displayed week (ISO date)
}

type Phase = "pickWeek" | "review" | "progress";

export function CopyWeekDialog({isOpen, onClose, targetAnchor}: CopyWeekDialogProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const impersonate = useAppSelector((s) => s.impersonation.impersonatedUser);
    const currentUserName = useAppSelector((s) => s.user.user.name);
    const defaultCategory = useAppSelector((s) => s.user.preferences.defaultCategory) || DEFAULT_CATEGORY;

    const [phase, setPhase] = useState<Phase>("pickWeek");
    const [offsetWeeks, setOffsetWeeks] = useState(1);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState<WeekOverviewSummary | null>(null);
    const [rows, setRows] = useState<WorkingRow[]>([]);
    const [targetDates, setTargetDates] = useState<string[]>([]);
    const [targetWeek, setTargetWeek] = useState<Week | null>(null);
    const [jiraBaseUrl, setJiraBaseUrl] = useState("");
    const [favorites, setFavorites] = useState<FavoriteEntry[]>([]);
    const [progress, setProgress] = useState({done: 0, total: 0, message: ""});
    const [validationError, setValidationError] = useState("");

    const sourceAnchor = addDaysToAPIDate(targetAnchor, -7 * offsetWeeks);

    useEffect(() => {
        if (!isOpen) { return; }
        setPhase("pickWeek");
        setOffsetWeeks(1);
        setValidationError("");
        setFavorites(loadFavorites(currentUserName));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || phase !== "pickWeek") { return; }
        let cancelled = false;
        setPreviewLoading(true);
        api.getTimeTable(sourceAnchor, "weekly", impersonate).then((res) => {
            if (cancelled) { return; }
            setPreview(summarizeWeekForOverview(res.data.weeks[0]));
        }).catch(() => {
            if (!cancelled) { setPreview(null); }
        }).finally(() => { if (!cancelled) { setPreviewLoading(false); } });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, phase, sourceAnchor, impersonate]);

    async function handleContinue() {
        try {
            const [sourceRes, targetRes] = await Promise.all([
                api.getTimeTable(sourceAnchor, "weekly", impersonate),
                api.getTimeTable(targetAnchor, "weekly", impersonate)
            ]);
            const sourceWeek: Week = sourceRes.data.weeks[0];
            const targetWeekData: Week = targetRes.data.weeks[0];
            const {rows: builtRows, blockedJiraIds} = buildCopyRows(sourceWeek, targetWeekData, (jiraId) => isAbsenceCodeBlocked(jiraId, currentUserName));

            if (blockedJiraIds.length > 0) {
                showSwal("failed", `${absenceBlockMessage(currentUserName)}\n\nSkipped code(s): ${blockedJiraIds.join(", ")}`);
            }
            if (builtRows.length === 0) {
                showSwal("failed", "Source week contains no booked entries.");
                return;
            }

            setTargetWeek(targetWeekData);
            setTargetDates(targetWeekData.week_dates);
            setJiraBaseUrl(sourceRes.data.JIRA_BASE_URL || targetRes.data.JIRA_BASE_URL || "");
            setRows(buildWorkingRows(builtRows));
            setPhase("review");
        } catch (err: any) {
            showSwal("failed", err?.response?.data ?? String(err));
        }
    }

    async function handleCopy() {
        if (!targetWeek) { return; }
        const result = rowsToResult(rows, "copy");
        if (result === null) {
            setValidationError("Hours must be empty or numbers between 0 and 24.");
            return;
        }
        setValidationError("");
        const {actions, conflicts} = buildCopyActions(result, targetWeek, targetDates);

        if (conflicts.length > 0) {
            const lines = conflicts.slice(0, 12).map((c) => `\u2022 ${c.jiraId} on ${c.day} (${c.date})${c.comment ? ` \u2014 "${c.comment}"` : ""}`);
            showSwal("failed", `${conflicts.length} entr${conflicts.length === 1 ? "y" : "ies"} conflict with an existing not-approved booking:\n\n${lines.join("\n")}`);
            return;
        }
        if (actions.length === 0) {
            showSwal("failed", "No positive hour values are left in the table.");
            return;
        }

        setPhase("progress");
        dispatch(setTimeTableLoading(true));
        setProgress({done: 0, total: actions.length, message: "Starting..."});
        const failures: {jiraId: string; date: string; reason: string}[] = [];
        try {
            for (let i = 0; i < actions.length; i += 1) {
                const action = actions[i];
                setProgress({done: i, total: actions.length, message: `${action.jiraId} ${action.date}`});
                try {
                    await saveWorklogEntry({jiraId: action.jiraId, category: defaultCategory, date: action.date, hours: action.hours, comment: action.comment, impersonate});
                } catch (err: any) {
                    console.error("WeekActions: copy failed for", action.jiraId, action.date, err);
                    failures.push({jiraId: action.jiraId, date: action.date, reason: err?.response?.data ? String(err.response.data) : String(err)});
                }
                setProgress({done: i + 1, total: actions.length, message: `${action.jiraId} ${action.date}`});
            }
        } finally {
            dispatch(timetableRefresh());
            dispatch(jiraBoardrefreshNofilters());
            dispatch(setTimeTableLoading(false));
            setPhase("pickWeek");
            onClose();
        }
        if (failures.length > 0) {
            const lines = failures.slice(0, 12).map((f) => `\u2022 ${f.jiraId} on ${f.date} \u2014 ${f.reason}`);
            showSwal("failed", `${failures.length} of ${actions.length} entries failed to copy and were skipped (the rest were copied):\n\n${lines.join("\n")}`);
        }
    }

    if (phase === "progress") {
        return <ProgressOverlay isOpen title="Copying entries..." done={progress.done} total={progress.total} message={progress.message}/>;
    }

    if (phase === "pickWeek") {
        const sourceSunday = addDaysToAPIDate(sourceAnchor, 6);
        return (
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl">
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>Select Source Week</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <InfoBar>
                            <Text><b>Target week:</b> {targetAnchor} to {addDaysToAPIDate(targetAnchor, 6)}</Text>
                            <Text><b>Source week:</b> {sourceAnchor} to {sourceSunday}</Text>
                        </InfoBar>
                        <HStack justify="space-between" mb={3}>
                            <Button size="sm" onClick={() => setOffsetWeeks((v) => v + 1)}>&lt; Older</Button>
                            <Text fontWeight={600}>Week {getIsoWeekNumber(sourceAnchor)} ({sourceAnchor} to {sourceSunday})</Text>
                            <Button size="sm" isDisabled={offsetWeeks === 1} onClick={() => setOffsetWeeks((v) => Math.max(1, v - 1))}>Newer &gt;</Button>
                        </HStack>
                        {previewLoading ? (
                            <Spinner/>
                        ) : preview ? (
                            <WeekPreviewTable preview={preview}/>
                        ) : (
                            <Text color="red.600">Unable to load source-week preview.</Text>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleContinue}>Continue</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl">
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>Review And Edit Week Copy</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <InfoBar>
                            <Text>From: {sourceAnchor} to {addDaysToAPIDate(sourceAnchor, 4)}</Text>
                            <Text>To: {targetAnchor} to {addDaysToAPIDate(targetAnchor, 4)}</Text>
                            <Text>Category: {defaultCategory}</Text>
                        </InfoBar>
                        {validationError && (
                            <Alert status="error" mb={3}><AlertIcon/>{validationError}</Alert>
                        )}
                        <EditableWeekTable
                            rows={rows}
                            setRows={setRows}
                            mode="copy"
                            jiraBaseUrl={jiraBaseUrl}
                            favorites={favorites}
                            onManageFavorites={() => navigate("/preferences")}
                        />
                        <Text mt={2} fontSize="xs" color="gray.600">Gray cells already have a booking in the current week and are excluded from copying.</Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleCopy}>Copy</Button>
                    </ModalFooter>
                </ModalContent>
        </Modal>
    );
}

function WeekPreviewTable({preview}: {preview: WeekOverviewSummary}) {
    return (
        <Box>
            <Text fontSize="sm" mb={2}>
                Entries: {preview.entryCount} | Total: {formatHourString(preview.totalHours)}h | Mon-Fri: {formatHourString(preview.weekdayHours)}h
            </Text>
            <TableContainer maxH="360px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                <Table size="sm">
                    <Thead position="sticky" top={0} bg="gray.100">
                        <Tr>
                            <Th>Key</Th>
                            <Th>Summary</Th>
                            {WEEKDAY_NAMES.map((d) => <Th key={d} textAlign="center">{d}</Th>)}
                            <Th textAlign="center">Total</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {preview.rows.length === 0 ? (
                            <Tr><Td colSpan={10} textAlign="center" color="gray.500">No booked entries in this week.</Td></Tr>
                        ) : preview.rows.map((row) => (
                            <Tr key={row.jiraId}>
                                <Td color="blue.600" fontWeight={600}>{row.jiraId}</Td>
                                <Td>{row.summary}</Td>
                                {row.hours.map((h, i) => <Td key={i} textAlign="center">{h > 0 ? `${formatHourString(h)}h` : ""}</Td>)}
                                <Td textAlign="center" fontWeight={600}>{formatHourString(row.rowSum)}h</Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>
        </Box>
    );
}
